import { createContext, ReactNode, useContext, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { getCookie } from 'cookies-next';

import useStorage from '@internal/hooks/use-storage';

import { UtmParams } from './utm-context.types';
import { decodeUtmParams, extractValidQueries } from './utm-context.utils';

type UtmContextType = {
  getUtmParams: () => UtmParams | null;
};

const UtmContext = createContext<UtmContextType>({} as UtmContextType);

type UtmStorageSchema = {
  values: UtmParams;
};

export const useUtmParamsCapture = (): (() => UtmParams | null) => {
  const localStorage = useStorage<UtmStorageSchema>({
    domainKey: 'utm-params',
    type: 'local',
  });
  const router = useRouter();

  const utmParams = useRef<UtmParams | null>(null);

  useEffect(() => {
    const cookieUtmParams = getCookie('utmParams');
    if (cookieUtmParams) {
      const parsedCookieUtmParams = JSON.parse(
        cookieUtmParams as string
      ) as UtmParams;
      utmParams.current = decodeUtmParams(parsedCookieUtmParams);
      localStorage.setValue('values', decodeUtmParams(parsedCookieUtmParams));
      return;
    }

    const storedUtmParams = localStorage.getValue('values');
    if (storedUtmParams) {
      utmParams.current = storedUtmParams;
      return;
    }

    if (router.isReady) {
      const queryParams = extractValidQueries(router.query);

      if (Object.keys(queryParams).length) {
        utmParams.current = queryParams;
        localStorage.setValue('values', queryParams);
        return;
      }
    }
  }, [router.isReady, router.query, localStorage]);

  return () => utmParams.current;
};

export const UtmContextProvider = ({ children }: { children: ReactNode }) => {
  const getUtmParams = useUtmParamsCapture();

  return (
    <UtmContext.Provider
      value={{
        getUtmParams,
      }}
    >
      {children}
    </UtmContext.Provider>
  );
};

export const useUtmContext = () => {
  const utmContext = useContext(UtmContext);

  return utmContext;
};
